import { ColorDynamic } from '@superdispatch/ui';
import { ButtonProps } from '@superdispatch/ui-lab';
import { isEmpty, omitBy } from 'es-toolkit/compat';
import { ReactNode } from 'react';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import {
  canHaveSimilarSearch,
  createSearchCriteria,
  createSimilarSearch,
  searchCriteriaToJSON,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { PermissionIcon } from 'shared/modules/permission/core/PermissionIcon';
import { PermissionDTO } from 'shared/modules/permission/PermissionDTO';
import {
  CarrierSettings,
  FeatureToggle,
} from 'shared/settings/CarrierSettingsAPI';
import { formatLoadStatus, isLoadCanceled, LoadDTO } from '../data/LoadDTO';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import { LoadsPageLoadAction } from '../loads-page/data/LoadsActions';
import { LoadCreationLockIcon } from '../loads-page/core/LoadCreationLockIcon';

export type ViewLoadAction = LoadsPageLoadAction | 'view_invoice';

export interface ViewLoadActionProps {
  load: LoadDTO;
  settings?: CarrierSettings;
  loadStage: string;
  isMobile: boolean;
  isFeatureToggleEnabled: (feature: FeatureToggle) => boolean;
  isSubhauled: boolean;
}

interface ViewLoadActionOption {
  label: ReactNode | ((props: ViewLoadActionProps) => ReactNode);
  action?: (props: ViewLoadActionProps) => ViewLoadAction;
  onClick?: (props: ViewLoadActionProps) => void;
  isPrimary?: (props: ViewLoadActionProps) => boolean;
  isVisible?: (props: ViewLoadActionProps) => boolean;
  isDisabled?: (props: ViewLoadActionProps) => boolean;
  buttonProps?: (props: ViewLoadActionProps) => Partial<ButtonProps>;
  dataIntercomTarget?: string;
  permission?: keyof NonNullable<PermissionDTO['loads']>;
  endIcon?: ReactNode;
  startIcon?: ReactNode;
}

interface ViewLoadActionGroup {
  options: ViewLoadActionOption[];
  isVisible?: (props: ViewLoadActionProps) => boolean;
}

export const viewLoadActions: ViewLoadActionGroup[] = [
  {
    isVisible: ({ load, isSubhauled }) =>
      !!load.is_dispatched_to_carrier && !isSubhauled,
    options: [
      {
        label: 'Open Dispatched Load',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        onClick: ({ load }) => {
          openExternalURL(load.url_in_stms, { target: '_blank' });
        },
      },
      {
        isVisible: ({ isSubhauled }) => !isSubhauled,
        label: 'Remove "Dispatched to Carrier"',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'remove_dispatched_to_carrier_label',
      },
    ],
  },
  {
    isVisible: ({ load, isMobile, isSubhauled }) =>
      !!load.can_be_edited && isMobile && !isSubhauled,
    options: [
      {
        label: 'Edit',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        onClick: ({ load, loadStage }) => {
          openExternalURL(`/tms/loads/${load.guid}/edit`, { target: '_blank' });
          trackLoadsEvent({
            name: 'Carrier Clicked Edit Load',
            page: 'view_load',
            is_created_by_broker: !!load.is_created_by_broker,
            load_status: formatLoadStatus(load.status),
            utm_medium: loadStage,
          });
        },
      },
    ],
  },

  {
    isVisible: ({ load, settings }) =>
      !!settings?.has_terminal && !load.archived && !!load.is_active,

    options: [
      {
        label: 'Mark as in Terminal',
        action: () => 'mark_as_in_terminal',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load, isSubhauled }) =>
          load.terminals &&
          !!load.terminals.length &&
          !!load.can_mark_as_in_terminal &&
          !isSubhauled,
      },
      {
        label: 'Add Terminal',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'add_terminal',
        isVisible: ({ load, isSubhauled }) =>
          !!load.can_add_terminal &&
          !load.is_received_via_subhaul &&
          !isSubhauled,
      },
      {
        label: 'Remove Terminal',
        action: () => 'remove_terminal',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load }) =>
          load.terminals &&
          load.terminals.some((terminal) => !terminal.is_delivered),
      },
    ],
  },
  {
    isVisible: ({ load }) => !!load.is_active,

    options: [
      {
        label: 'Assign Driver',
        dataIntercomTarget: 'view-load-assign-driver-button',
        permission: 'can_manage',
        startIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            fontSize="medium"
          />
        ),
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: ({ load }) => (load.trip?.name ? 'assign_trip' : 'assign'),
        isVisible: ({ load, isSubhauled }) =>
          !load.archived && !!load.can_be_assigned_to_driver && !isSubhauled,
        isPrimary: ({ load }) => !load.driver && load.status === 'new',
      },

      {
        label: 'Reassign',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: ({ load }) => (load.trip?.name ? 'reassign' : 'assign'),
        isVisible: ({ load, isSubhauled }) =>
          !!load.can_be_unassigned_from_driver && !isSubhauled,
      },

      {
        label: 'Unassign',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'unassign',
        isVisible: ({ load, isSubhauled }) =>
          !load.archived &&
          !!load.can_be_unassigned_from_driver &&
          !isSubhauled,
      },

      {
        label: 'Duplicate',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          >
            <LoadCreationLockIcon />
          </PermissionIcon>
        ),
        action: ({ settings }) =>
          settings?.can_create_load ? 'duplicate' : 'upgrade_plan',
        isVisible: ({ load, isSubhauled, isFeatureToggleEnabled }) =>
          !load.archived &&
          !!load.can_be_duplicated &&
          !load.is_received_via_subhaul &&
          !isSubhauled &&
          (!load.is_edi ||
            isFeatureToggleEnabled('carrier_duplicate_edi_load')),
      },

      {
        label: 'Split Order',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load, isSubhauled, isFeatureToggleEnabled }) =>
          !load.archived &&
          !!load.can_be_split &&
          !load.is_received_via_subhaul &&
          !isSubhauled &&
          (!load.is_edi ||
            isFeatureToggleEnabled('carrier_duplicate_edi_load')),
        onClick: ({ load, loadStage }) => {
          trackLoadsEvent({
            name: 'Carrier Clicked Split Order',
            page: 'view_load',
            utm_medium: loadStage,
            is_created_by_broker: !!load.is_created_by_broker,
          });
          openExternalURL(
            '/tms/loads-split/{guid}{?back_url,is_created_by_broker,utm_medium}',
            {
              guid: load.guid,
              back_url: window.location.pathname + window.location.search,
              utm_medium: loadStage,
              is_created_by_broker: load.is_created_by_broker,
            },
          );
        },
      },

      {
        label: 'Sub-haul',
        action: () => 'subhaul',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load, isFeatureToggleEnabled }) =>
          !!load.can_be_subhauled && isFeatureToggleEnabled('ctms_edi_subhaul'),
      },

      {
        label: ({ isFeatureToggleEnabled, load }) =>
          isFeatureToggleEnabled('ctms_loads_new')
            ? load.driver?.guid
              ? 'Move to Assigned'
              : 'Move to Unassigned'
            : 'Mark as New',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: ({ load, isFeatureToggleEnabled }) =>
          isFeatureToggleEnabled('ctms_loads_new')
            ? load.driver?.guid
              ? 'move_to_assigned'
              : 'move_to_unassigned'
            : 'mark_as_new',
        buttonProps: () => ({ variant: 'neutral' }),
        isVisible: ({ load, isSubhauled }) => {
          if (isSubhauled) {
            return false;
          }
          if (load.payment_details?.payment_method === 'superpay') {
            return load.status === 'picked_up';
          }

          return load.status !== 'new';
        },
      },

      {
        label: 'Mark as Picked Up',
        permission: 'can_mark_as_picked_up',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_mark_as_picked_up"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'mark_as_picked_up',
        buttonProps: () => ({ variant: 'neutral' }),
        isVisible: ({ load, isSubhauled }) => {
          if (isSubhauled) {
            return false;
          }
          const isVisible = !load.archived && load.can_be_picked_up;

          if (load.payment_details?.payment_method === 'superpay') {
            return load.status === 'new' && isVisible;
          }

          return isVisible;
        },
      },

      {
        label: 'Mark as Delivered',
        permission: 'can_mark_as_delivered',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_mark_as_delivered"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'mark_as_delivered',
        buttonProps: () => ({ variant: 'neutral' }),
        isVisible: ({ load, isSubhauled }) =>
          !load.archived &&
          !!load.can_be_delivered &&
          !load.can_mark_as_in_terminal &&
          !isSubhauled,
      },
      {
        label: 'Dispatch to Carrier',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'dispatch_to_carrier',
        isVisible: ({ load, isSubhauled }) =>
          !load.archived &&
          !!load.can_be_dispatched_to_carrier &&
          !load.should_request_dispatch_to_carrier &&
          !isSubhauled,
      },

      {
        label: 'Send BOL',
        permission: 'can_send_bol',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_send_bol"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'send_bol',
        isVisible: ({ load }) => load.can_send_bol,
        onClick: ({ load, loadStage }) => {
          trackLoadsEvent({
            name: 'Carrier Clicked Send BOL',
            page: 'view_load',
            utm_medium: loadStage,
            status: load.status,
            is_canceled_by: load.is_canceled_by,
          });
        },
      },

      {
        label: 'Move to Vehicles',
        action: () => 'move_to_vehicles',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load, isFeatureToggleEnabled, isSubhauled }) =>
          !!load.is_edi &&
          isFeatureToggleEnabled('ctms_edi_vehicles') &&
          !isSubhauled,
        onClick: ({ load, loadStage }) => {
          trackLoadsEvent({
            name: 'Carrier Clicked Move to Vehicles',
            page: 'view_load',
            utm_medium: loadStage,
            status: load.status,
          });
        },
      },

      {
        label: 'Send Invoice',
        permission: 'can_send_invoice',
        startIcon: (
          <PermissionIcon
            name="loads"
            permission="can_send_invoice"
            fontSize="medium"
          />
        ),
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_send_invoice"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'send_invoice_drawer',
        isPrimary: ({ load }) =>
          load.payments &&
          load.payments.length > 0 &&
          load.status === 'delivered' &&
          !load.payments[0]?.invoice.sent_at &&
          !load.payments[0]?.paid_at,
        isVisible: ({ load, isSubhauled }) =>
          !!load.can_send_invoice ||
          (!!isSubhauled && !!load.can_invoice_be_sent),
        onClick: ({ load, loadStage }) => {
          trackLoadsEvent({
            name: 'CTMS: Send Invoice Clicked',
            page: 'view_load',
            load_stage: load.archived ? 'archived' : undefined,
            utm_medium: loadStage,
            status: load.status,
            is_canceled_by: load.is_canceled_by,
          });
        },
      },

      {
        label: ({ isSubhauled }) =>
          isSubhauled ? 'Mark as Paid by Customer' : 'Mark as Paid',
        action: () => 'mark_as_paid',
        permission: 'can_mark_as_paid',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_mark_as_paid"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        startIcon: (
          <PermissionIcon
            name="loads"
            permission="can_mark_as_paid"
            fontSize="medium"
          />
        ),
        isPrimary: ({ load }) =>
          load.payments &&
          load.payments.length > 0 &&
          !!load.payments[0]?.invoice.sent_at,
        isVisible: ({ load, isSubhauled }) =>
          (!load.archived && !!load.can_be_marked_as_paid) ||
          (!load.archived &&
            isSubhauled &&
            !!load.can_be_marked_as_paid_by_customer),
      },
      {
        label: 'Mark as Paid to Sub-Hauler',
        action: () => 'mark_as_paid_to_subhauler',
        permission: 'can_mark_as_paid',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_mark_as_paid"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        startIcon: (
          <PermissionIcon
            name="loads"
            permission="can_mark_as_paid"
            fontSize="medium"
          />
        ),
        isVisible: ({ load, isSubhauled }) =>
          !load.archived &&
          isSubhauled &&
          !!load.can_be_marked_as_paid_to_subhauler,
      },

      {
        label: 'Mark as Unpaid to Sub-Hauler',
        action: () => 'mark_as_unpaid_to_subhauler',
        permission: 'can_mark_as_paid',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_mark_as_paid"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        startIcon: (
          <PermissionIcon
            name="loads"
            permission="can_mark_as_paid"
            fontSize="medium"
          />
        ),
        isVisible: ({ load, isSubhauled }) =>
          !load.archived &&
          isSubhauled &&
          !!load.can_be_marked_as_unpaid_to_subhauler,
      },
      {
        label: ({ isSubhauled }) =>
          isSubhauled ? 'Mark as Unpaid by Customer' : 'Mark as Unpaid',
        permission: 'can_mark_as_paid',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_mark_as_paid"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'mark_as_unpaid',
        isVisible: ({ load, isSubhauled }) =>
          !!load.can_be_marked_as_unpaid ||
          (!load.archived &&
            isSubhauled &&
            !!load.can_be_marked_as_unpaid_by_customer),
      },
    ],
  },
  {
    options: [
      {
        label: 'View BOL',
        action: ({ isSubhauled }) =>
          isSubhauled ? 'view_subhauler_bol' : 'view_bol',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_view_bol"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        permission: 'can_view_bol',
        isVisible: ({ load, isSubhauled }) =>
          load.can_view_bol || (!!isSubhauled && !!load.can_bol_be_viewed),
        onClick: ({ loadStage, load }) => {
          trackLoadsEvent({
            name: 'CTMS: Clicked View BOL',
            page: 'view_load',
            utm_medium: loadStage,
            status: load.status,
            is_canceled_by: load.is_canceled_by,
          });
        },
      },
      {
        label: 'View Online BOL',
        action: () => 'view_online_bol',
        isVisible: ({ load, isSubhauled }) =>
          !!isSubhauled && !!load.can_online_bol_be_viewed,
        onClick: ({ load }) => {
          openExternalURL(load.online_bol_url, { target: '_blank' });
        },
      },
      {
        label: 'View Invoice',
        action: () => 'view_invoice',
        permission: 'can_send_invoice',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_send_invoice"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load, isSubhauled }) =>
          load.can_view_invoice && !isSubhauled,
        onClick: ({ loadStage, load }) => {
          trackLoadsEvent({
            name: 'CTMS: View Invoice Clicked',
            page: 'view_load',
            utm_medium: loadStage,
            status: load.status,
            is_canceled_by: load.is_canceled_by,
          });
        },
      },
    ],
  },
  {
    isVisible: ({ load, isSubhauled }) =>
      !!load.can_be_revoked && !!isSubhauled,
    options: [
      {
        label: 'Revoke Load',
        action: () => 'revoke',
      },
    ],
  },
  {
    isVisible: ({ load, isSubhauled }) =>
      !load.archived && !!load.is_active && !isSubhauled,
    options: [
      {
        label: 'Search for Similar Loads',
        isDisabled: ({ load }) =>
          !canHaveSimilarSearch(
            createSearchCriteria({
              pickup_venues: [load.pickup.venue],
              delivery_venues: [load.delivery.venue],
            }),
          ),
        onClick: ({ load }) => {
          trackLoadsEvent({
            name: 'CTMS: Search for Similar Loads Clicked',
            page: 'view_load',
          });
          openExternalURL(
            '/loadboard/loads{?utm_medium,utm_source,search_criteria}',
            {
              utm_source: 'Web CTMS',
              utm_medium: 'Similar Loads',
              search_criteria: searchCriteriaToJSON(
                createSimilarSearch(
                  createSearchCriteria({
                    pickup_venues: [omitBy(load.pickup.venue, isEmpty)],
                    delivery_venues: [omitBy(load.delivery.venue, isEmpty)],
                  }),
                ),
              ),
            },
          );
        },
      },
    ],
  },

  {
    options: [
      {
        label: 'Archive',
        action: () => 'archive',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        startIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            fontSize="medium"
          />
        ),
        buttonProps: () => ({ variant: 'neutral' }),
        isPrimary: ({ load }) =>
          !!load.payments[0]?.paid_at && load.status === 'delivered',
        isVisible: ({ load, isSubhauled }) => {
          if (load.is_received_via_subhaul) {
            return (
              !load.archived &&
              !!load.is_active &&
              (!load.is_created_by_broker ||
                isLoadCanceled(load) ||
                load.status !== 'new') &&
              load.status !== 'new' &&
              load.status !== 'picked_up'
            );
          }
          return (
            !load.archived &&
            !!load.is_active &&
            (!load.is_created_by_broker ||
              isLoadCanceled(load) ||
              load.status !== 'new') &&
            !isSubhauled
          );
        },
      },
      {
        label: 'Unarchive',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'unarchive',
        isVisible: ({ load, isSubhauled }) =>
          !!load.archived && !!load.is_active && !isSubhauled,
      },
      {
        label: 'Delete',
        action: () => 'delete',
        permission: 'can_delete',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_delete"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load, isSubhauled }) => {
          if (load.is_received_via_subhaul) {
            return (
              load.is_active &&
              (!load.is_created_by_broker ||
                isLoadCanceled(load) ||
                load.status !== 'new') &&
              load.status !== 'new' &&
              load.status !== 'picked_up'
            );
          }
          return (
            load.is_active &&
            (!load.is_created_by_broker ||
              isLoadCanceled(load) ||
              load.status !== 'new') &&
            !isSubhauled
          );
        },
      },
      {
        label: 'Cancel Load',
        action: () => 'cancel',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load, isSubhauled }) =>
          load.is_created_by_broker &&
          !isLoadCanceled(load) &&
          load.is_active &&
          load.status === 'new' &&
          !isSubhauled,
      },
      {
        label: 'Restore',
        action: () => 'restore',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        startIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            fontSize="medium"
          />
        ),
        isPrimary: ({ load }) => !load.is_active,
        isVisible: ({ load, isSubhauled }) => !load.is_active && !isSubhauled,
        buttonProps: () => ({ variant: 'neutral' }),
      },
    ],
  },
];
